/**
 * Action Center — the living part of a record.
 *
 * A record's fields explain what something is. This is where a user says what
 * happened and what should happen next, in one pass: write (or dictate) a note,
 * adjust whatever this record type lets you adjust, press Update once.
 *
 * Two rules make it what it is, and both are load-bearing:
 *
 *   1. Nothing saves until Update. Every control inside is local form state.
 *      A checkbox that writes the moment it is clicked turns "let me look at
 *      this" into a permanent change, and a queue is exactly where people click
 *      to look.
 *   2. The note is the middle of the component. Follow-ups, toggles, and
 *      record-specific buttons support it; none of them may grow big enough to
 *      make writing a sentence feel like the secondary option.
 *
 * Record types configure it by composition rather than by schema: pass the
 * controls that apply as children, the record-specific buttons as `actions`,
 * and own the submit yourself. There is deliberately no notion here of leads,
 * calls, tasks, or workflow stages — this is an interaction primitive.
 */

import {
  useCallback,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from 'react';
import { AlertCircle, Check, Loader2, Mic, Square } from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '../lib/utils';
import { useSpeechDictation } from '../hooks/use-speech-dictation';

export interface ActionCenterProps {
  /** Section heading. Defaults to the component's own name. */
  heading?: string;
  /**
   * One line naming what this record needs next, when the caller knows. Purely
   * advisory — it never gates or preselects anything below it.
   */
  recommendation?: ReactNode;
  /** Draft note text. Owned by the caller so submit can read it. */
  note: string;
  onNoteChange: (value: string) => void;
  notePlaceholder?: string;
  /** Visually-hidden label for the textarea. */
  noteLabel?: string;
  noteRows?: number;
  /** Offer dictation. Silently absent on browsers without the Speech API. */
  voice?: boolean;
  /** Optional controls between the note and the submit row. */
  children?: ReactNode;
  /**
   * Record-specific buttons (Create Lead, Link Lead, Add Time…). They sit
   * beside Update, not above the note.
   */
  actions?: ReactNode;
  submitLabel?: string;
  pendingLabel?: string;
  onSubmit: () => void | Promise<void>;
  /** True when there is nothing to submit — no note, no changed control. */
  disabled?: boolean;
  pending?: boolean;
  error?: string | null;
  /** Shown briefly after a successful submit. */
  success?: string | null;
  className?: string;
}

export function ActionCenter({
  heading = 'Action Center',
  recommendation,
  note,
  onNoteChange,
  notePlaceholder = 'Add a note…',
  noteLabel = 'Notes',
  noteRows = 3,
  voice = true,
  children,
  actions,
  submitLabel = 'Update',
  pendingLabel = 'Saving…',
  onSubmit,
  disabled = false,
  pending = false,
  error,
  success,
  className,
}: ActionCenterProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  /*
    Dictation appends to whatever was already typed rather than replacing it.
    The hook reports the whole transcript for the current session each time, so
    the text present when the mic opened is captured and the transcript is
    concatenated onto it — otherwise a second dictation erases the first.
  */
  const baseRef = useRef('');
  const dictation = useSpeechDictation(
    useCallback(
      (transcript: string) => {
        const base = baseRef.current;
        onNoteChange(base ? `${base.replace(/\s+$/, '')} ${transcript}` : transcript);
      },
      [onNoteChange],
    ),
  );

  const toggleDictation = () => {
    if (dictation.listening) {
      dictation.stop();
      return;
    }
    baseRef.current = note;
    dictation.start();
    textareaRef.current?.focus();
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (disabled || pending) return;
    if (dictation.listening) dictation.stop();
    void onSubmit();
  };

  const showVoice = voice && dictation.supported;

  return (
    <form
      onSubmit={submit}
      className={cn(
        'rounded-lg border border-primary bg-selection p-4',
        className,
      )}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <h3 className="text-sm font-semibold text-foreground">{heading}</h3>
        {recommendation && (
          <p className="text-xs text-muted-foreground">{recommendation}</p>
        )}
      </div>

      <div className="relative mt-3">
        <textarea
          ref={textareaRef}
          aria-label={noteLabel}
          value={note}
          onChange={(event) => onNoteChange(event.target.value)}
          placeholder={notePlaceholder}
          rows={noteRows}
          disabled={pending}
          className={cn(
            'w-full resize-y rounded-md border border-border bg-card px-3 py-2 text-sm leading-relaxed text-foreground outline-none transition-shadow',
            'placeholder:text-muted-foreground focus:ring-2 focus:ring-primary/30 disabled:opacity-60',
            showVoice && 'pr-11',
          )}
        />
        {showVoice && (
          <button
            type="button"
            onClick={toggleDictation}
            disabled={pending}
            aria-label={dictation.listening ? 'Stop dictation' : 'Dictate a note'}
            aria-pressed={dictation.listening}
            title={dictation.listening ? 'Stop dictation' : 'Dictate a note'}
            className={cn(
              'absolute right-2 top-2 inline-flex h-8 w-8 items-center justify-center rounded-md border transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50',
              dictation.listening
                ? 'border-destructive/40 bg-destructive/10 text-destructive'
                : 'border-border bg-card text-muted-foreground hover:text-foreground',
            )}
          >
            {dictation.listening ? (
              <Square className="h-3.5 w-3.5 fill-current" aria-hidden />
            ) : (
              <Mic className="h-4 w-4" aria-hidden />
            )}
          </button>
        )}
      </div>
      {dictation.listening && (
        <p className="mt-1.5 text-xs text-muted-foreground" role="status">
          Listening — speak, then press stop.
        </p>
      )}

      {children && <div className="mt-3 space-y-3">{children}</div>}

      {error && (
        <p
          className="mt-3 flex items-start gap-1.5 text-sm text-destructive"
          role="alert"
        >
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          <span>{error}</span>
        </p>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-border/70 pt-3">
        {actions}
        <div className="ml-auto flex items-center gap-2">
          {success && !pending && (
            <span
              className="inline-flex items-center gap-1 text-xs font-medium text-success"
              role="status"
            >
              <Check className="h-3.5 w-3.5" aria-hidden />
              {success}
            </span>
          )}
          <Button type="submit" size="sm" disabled={disabled || pending}>
            {pending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden />}
            {pending ? pendingLabel : submitLabel}
          </Button>
        </div>
      </div>
    </form>
  );
}

/**
 * A control that finishes a record, and asks once before it does.
 *
 * Terminal actions remove a record from the queue the user is working, so they
 * need a beat — but a modal for one word is heavier than the decision. Pressing
 * it turns the button into its own confirmation in place: the label becomes the
 * consequence, and a Cancel appears beside it. Nothing moves on the page, and
 * the second press is on the same pixel as the first.
 *
 * It is deliberately quiet by default (`variant="outline"`), because these sit
 * beside Update and must not compete with it. Finishing a lead is the rarer
 * action; writing a note is the everyday one.
 */
export function ActionCenterTerminalButton({
  label,
  confirmLabel,
  icon: Icon,
  onConfirm,
  disabled,
  tone = 'neutral',
}: {
  label: string;
  /** What the button says once armed — name the consequence, not "Confirm". */
  confirmLabel: string;
  icon?: React.ComponentType<{ className?: string }>;
  onConfirm: () => void;
  disabled?: boolean;
  /**
   * `danger` for the action that discards work; `success` for the one that
   * completes it. Neutral is the default because most terminal actions are
   * neither — they just end something.
   *
   * `success` deliberately tints the RESTING button too, not only the armed
   * confirmation. A completion action is the one thing on the panel a user is
   * looking for, and an outline button beside a Dismiss button reads as the
   * lesser of two exits rather than as the goal.
   */
  tone?: 'neutral' | 'danger' | 'success';
}) {
  const [armed, setArmed] = useState(false);

  if (!armed) {
    return (
      <Button
        type="button"
        size="sm"
        variant="outline"
        disabled={disabled}
        className={cn(
          'gap-1.5',
          tone === 'success'
            && 'border-success/40 text-success hover:bg-success/10 hover:text-success',
        )}
        onClick={() => setArmed(true)}
      >
        {Icon && <Icon className="h-3.5 w-3.5" aria-hidden />}
        {label}
      </Button>
    );
  }

  return (
    <span className="inline-flex items-center gap-1.5">
      <Button
        type="button"
        size="sm"
        variant={tone === 'danger' ? 'destructive' : 'default'}
        disabled={disabled}
        className={cn(
          'gap-1.5',
          tone === 'success' && 'bg-success text-success-foreground hover:bg-success/90',
        )}
        onClick={() => {
          setArmed(false);
          onConfirm();
        }}
      >
        {Icon && <Icon className="h-3.5 w-3.5" aria-hidden />}
        {confirmLabel}
      </Button>
      <button
        type="button"
        onClick={() => setArmed(false)}
        className="rounded-md px-1.5 py-1 text-xs text-muted-foreground hover:text-foreground"
      >
        Cancel
      </button>
    </span>
  );
}

/** A labelled group of secondary controls, sized to sit under the note. */
export function ActionCenterField({
  label,
  children,
  className,
}: {
  label: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
        {label}
      </p>
      {children}
    </div>
  );
}

export function ActionCenterCheckbox({
  checked,
  onChange,
  label,
  description,
  disabled,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  description?: string;
  disabled?: boolean;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-2.5">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
        className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer rounded border-border accent-primary disabled:cursor-not-allowed"
      />
      <span className="min-w-0">
        <span className="block text-sm font-medium text-foreground">{label}</span>
        {description && (
          <span className="block text-xs text-muted-foreground">{description}</span>
        )}
      </span>
    </label>
  );
}

/**
 * The date this record comes back, offered two ways that write one value.
 *
 * The quick offsets and the date input are the same field, which is the whole
 * point: a queue that reads one date must not be fed by two controls that
 * disagree. Offsets are computed from today, not from the date already set —
 * "3 days" means three days from now, which is what someone pressing it in the
 * middle of a call means.
 *
 * `value` is a plain `YYYY-MM-DD` string so it drops straight into `<input
 * type="date">`; the caller converts to an instant when it submits.
 */
export function FollowUpPicker({
  value,
  onChange,
  offsets = [1, 3, 7],
  disabled,
  now,
  clearable = true,
}: {
  value: string;
  onChange: (value: string) => void;
  /** Quick offsets in days, in the order they are offered. */
  offsets?: number[];
  disabled?: boolean;
  /** Injectable clock, for tests. */
  now?: Date;
  clearable?: boolean;
}) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {offsets.map((days) => {
        const target = followUpOffsetDate(days, now);
        const active = value === target;
        return (
          <Button
            key={days}
            type="button"
            size="sm"
            variant={active ? 'default' : 'outline'}
            disabled={disabled}
            aria-pressed={active}
            onClick={() => onChange(active ? '' : target)}
            className="h-8"
          >
            {followUpOffsetLabel(days)}
          </Button>
        );
      })}
      <input
        type="date"
        aria-label="Follow-up date"
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        className="h-8 rounded-md border border-border bg-card px-2 text-sm text-foreground outline-none focus:ring-2 focus:ring-primary/30 disabled:opacity-60"
      />
      {clearable && value && !disabled && (
        <button
          type="button"
          onClick={() => onChange('')}
          className="rounded-md px-1.5 py-1 text-xs text-muted-foreground hover:text-foreground"
        >
          Clear
        </button>
      )}
    </div>
  );
}

/** `YYYY-MM-DD`, `days` from today, in the reader's own timezone. */
export function followUpOffsetDate(days: number, now: Date = new Date()): string {
  const target = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  target.setDate(target.getDate() + days);
  const month = `${target.getMonth() + 1}`.padStart(2, '0');
  const day = `${target.getDate()}`.padStart(2, '0');
  return `${target.getFullYear()}-${month}-${day}`;
}

export function followUpOffsetLabel(days: number): string {
  return days === 1 ? '1 Day' : `${days} Days`;
}

/**
 * `YYYY-MM-DD` → an instant, at the end of that local day.
 *
 * End of day rather than midnight because a follow-up set for Tuesday means
 * "some time on Tuesday": a midnight timestamp reads as already overdue for the
 * whole of the day it names.
 */
export function followUpDateToIso(value: string): string | null {
  if (!value) return null;
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day, 23, 59, 59, 999).toISOString();
}

/** An instant → the `YYYY-MM-DD` the pickers use, in the reader's timezone. */
export function isoToFollowUpDate(value: string | null | undefined): string {
  if (!value) return '';
  const ts = Date.parse(value);
  if (Number.isNaN(ts)) return '';
  const date = new Date(ts);
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}
